import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { icon, number } from "../../../config";
import CommonTabStrip from "../../../shared/components/CommonTabStrip/CommonTabStrip";
import { FormProvider } from "../FormContext";
import { FORM_TABS, FORM_TABS_KEYS } from "../FormsConstant";
import FormRenderer from "../Preview/FormRenderer";
import { FormCalculationProvider, useFormCalculation } from "../contexts/FormCalculationContext";
import { getInitialResponsesFromContent, useFormValidation } from "../forms.hooks";
import { useFormExpressionBuilder } from "../hooks/useFormExpressionBuilder";
import "./FormFiller.scss";

const FormFillerContent = ({
  isOpen,
  onClose,
  formData,
  formTitle = "Untitled Form",
  formDescription = "",
  onSubmit,
  formId,
  formTab,
  submitDetails,
}) => {
  const [formResponses, setFormResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getCalculationValue } = useFormCalculation();
  const [smartChipResponses, setSmartChipResponses] = useState({});
  // Use form validation hook
  const { areAllRequiredFieldsFilled } = useFormValidation(formData, formResponses, formTab);

  // Use expression builder hook to handle all expression logic
  useFormExpressionBuilder(formData, formResponses, setFormResponses, isOpen, formId, formTab);

  /**
   * When dialog opens, seed formResponses from pre-filled content (e.g. reopen to edit) so validation sees saved values
   * @author Sarthak Arora
   */
  useEffect(() => {
    if (!isOpen) return;
    const content = formData?.content || formData;
    const scValues = formData?.content?.smartChips;
    const scValuesObj = scValues
      ? Object.keys(scValues).reduce((acc, key) => {
          acc[key] = scValues[key]?.attributes?.default;
          return acc;
        }, {})
      : {};
    const { initial, scResponses } = getInitialResponsesFromContent(content, formTab);
    setFormResponses(initial);
    setSmartChipResponses({ ...scValuesObj, ...scResponses });
  }, [isOpen, formData?.content, formData?.id]);

  if (!isOpen) return null;

  const isSubmitDisabled = isSubmitting || !areAllRequiredFieldsFilled(smartChipResponses);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmit?.(formResponses, getCalculationValue, formTab);
    setIsSubmitting(false);
  };

  const handleFieldChange = (fieldId, value, smartChipId) => {
    setFormResponses((prev) => {
      if (prev[fieldId] === value) return prev;
      return { ...prev, [fieldId]: value };
    });
    if (smartChipId) {
      setSmartChipResponses((prev) => {
        if (prev[smartChipId] === value) return prev;
        return { ...prev, [smartChipId]: value };
      });
    }
  };

  const formRendererElement = (
    <FormRenderer
      formData={formData?.content}
      onFieldChange={handleFieldChange}
      formResponses={formResponses}
      smartChipResponses={smartChipResponses}
      setSmartChipResponses={setSmartChipResponses}
      isInteractive={true}
      formTab={formTab}
      submitDetails={submitDetails}
    />
  );

  return (
    <div>
      <Dialog
        onClose={onClose}
        title={
          <div className='form-filler-dialog-title d-flex align-items-center'>
            {icon.FORMS}
            <span className='form-renderer-title'>{formData?.title || formTitle}</span>
          </div>
        }
        width={1080}
        height={"100%"}
        className='form-filler-modal'>
        <div className='form-filler-content d-flex flex-column overflow-hidden p-0'>
          <div className='form-filler-body overflow-y-auto p-0'>{formRendererElement}</div>
        </div>
        <DialogActionsBar>
          <div className='d-flex justify-content-end gap-2 w-100 px-4 py-1'>
            <Button onClick={onClose} className='btn btn-ghost' disabled={isSubmitting}>
              Cancel
            </Button>
            <div className='d-flex flex-column align-items-end'>
              <Button onClick={handleSubmit} className={`btn ${isSubmitDisabled ? "btn-ghost" : "btn-primary"}`} disabled={isSubmitDisabled}>
                {isSubmitting ? "Submitting..." : formData?.taskId ? "Update Form" : "Submit Form"}
              </Button>
            </div>
          </div>
        </DialogActionsBar>
      </Dialog>
    </div>
  );
};

// Main FormFiller component with Context Provider
const FormFiller = (props) => {
  return (
    <FormCalculationProvider>
      <FormProvider>
        <FormFillerContent {...props} />
      </FormProvider>
    </FormCalculationProvider>
  );
};

export default FormFiller;
